/* eslint-disable jira/filename-case, jira/naming/disallow-filenames */
import React, { forwardRef, memo, type Ref } from 'react';
import { isShallowEqual } from '@jira/platform__utils-shallow-equal/src/index.tsx';
import { useSidebarNav4 } from '@jira/navigation-apps__sidebar-nav4-context/src/controllers/sidebar-context/index.tsx';
import { useSidebarNav4Controller } from '@jira/navigation-apps__sidebar-nav4-context/src/controllers/sidebar-controller-context/index.tsx';
import LinkMenuItem, {
	type LinkMenuItemWithAnalyticsProps,
} from '../link-menu-item-with-analytics/index.tsx';
import { getTestId } from '../../utils/get-test-id/index.tsx';
import type { Nav4DragAndDropMenuItemProps } from '../../types.tsx';

// oxlint-disable-next-line @typescript-eslint/no-explicit-any
export type Nav4MenuLinkItemProps<RouterLinkConfig extends Record<string, any> = never> = Omit<
	LinkMenuItemWithAnalyticsProps<RouterLinkConfig>,
	'isSelected'
> &
	Nav4DragAndDropMenuItemProps & {
		menuId: string;
	};

/**
 * Nav4MenuLinkItem provides `menuId` instead of `isSelected`,
 * which is used to set the selected-state of the underlying
 * LinkMenuItem, using the useSidebarNav4() context-hook.
 */
const Nav4MenuLinkItemForwardRef = forwardRef(Nav4MenuLinkItemContent);

// memoWithCondition reads the gate at render time (not module load) so it is
// SSR-safe and compatible with the feature access tracker. When the gate is off
// the component always re-renders (today's behaviour). When on, it bails out if
// all props are shallowly equal — stopping a parent re-render from cascading
// into the LinkMenuItem / IconButton / button-base / pressable subtree (the
// sidebar re-render waste measured via Chippy).
// oxlint-disable-next-line typescript/consistent-type-assertions
// oxlint-disable-next-line typescript/consistent-type-assertions
export const Nav4MenuLinkItem = memo(
	Nav4MenuLinkItemForwardRef,
	isShallowEqual,
	// oxlint-disable-next-line @typescript-eslint/no-explicit-any
) as <RouterLinkConfig extends Record<string, any> = never>(
	props: Nav4MenuLinkItemProps<RouterLinkConfig> & {
		ref?: Ref<HTMLAnchorElement>;
	},
) => ReturnType<typeof LinkMenuItem>;

// oxlint-disable-next-line @typescript-eslint/no-explicit-any
function Nav4MenuLinkItemContent<RouterLinkConfig extends Record<string, any> = never>(
	props: Nav4MenuLinkItemProps<RouterLinkConfig>,
	ref: Ref<HTMLAnchorElement>,
) {
	const { menuId } = props;
	// Suppressed due to new lint violation when upgrading to eslint-plugin-react-hooks v5

	const { isSelectedPath } = useSidebarNav4();
	// Suppressed due to new lint violation when upgrading to eslint-plugin-react-hooks v5

	const { isControlledPath } = useSidebarNav4Controller();

	return (
		<LinkMenuItem<RouterLinkConfig>
			ref={ref}
			{...props}
			isSelected={isSelectedPath(menuId) || isControlledPath(menuId)}
			testId={getTestId(menuId)}
		/>
	);
}
